import { Component, Inject, Input, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { IStateService } from 'angular-ui-router';
import { includes } from 'lodash';
import { Subject, SubscriptionLike as ISubscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { CsvExporterService } from 'Src/ng2/shared/services/csv-exporter/csv-exporter.service';
import { ISchool } from 'Src/ng2/shared/typings/interfaces/school.interface';
import { IUser } from 'src/ng2/shared/typings/interfaces/user.interface';
import { getCsvDataState, getSchool } from 'Src/ng2/store';
import { ICsvDataState } from 'src/ng2/store/reducers/csv-data-reducer';
import { unsubscribeComponent } from '../../shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { ModalsService } from '../../shared/modals/modals.service';
import { ImUser } from '../../shared/services/im-models/im-user';
import { ToggleBatchActions } from '../../store/actions/batch-actions-actions';
import { getBatchActionsMode, getBatchActionsSelectedStudentIds } from '../../store/selectors/batch-actions-selectors';
import { getCurrentUser } from './../../store/selectors/current-user-selectors';

export interface IListData {
  listType: string;
  groupings: any;
  madlibSelections: { focus: any; filter: any; grouping: any };
  columns: any[];
}

@Component({
  selector: 'content-tools',
  template: require('./content-tools.component.html'),
  styles: [require('./content-tools.component.scss')],
})
@unsubscribeComponent
export class ContentToolsComponent implements OnInit {
  @Input() isRefactoredList: boolean;
  @Input() listData: IListData;
  @Input() hideTools?: boolean;

  public students = [];
  public countStudents: number;
  public userCanEdit: boolean;
  public school: ISchool;
  public isListActionsState: boolean;
  public isListSummaryState: boolean;
  public isCsvExportState: boolean;
  public isBatchActionsState: boolean;
  public batchActionsMode: boolean;
  public currentUserSubscription: ISubscription;
  public batchActionsModeSubscription: ISubscription;
  public batchActionSelectedStudentIdsSubscription: ISubscription;
  public destroy$: Subject<boolean> = new Subject<boolean>();
  public csvStoreDataSubscription: ISubscription;
  public csvStoreData: ICsvDataState;
  public currentUser: IUser;

  readonly statesWithNoListActions: string[] = [
    'main.school.lists.dashboard',
    'main.school.lists.graph-detail',
    'main.school.lists.tiles',
    'main.school.lists.midLevel',
    'main.school.support-overview',
    'main.school.support-attendance-form',
    'main.school.my-supports',
    'main.network.studentLevel.detail',
  ];

  // include if a state only has a subset of list actions.
  readonly statesWithNoListSummary: string[] = ['main.school.lists.studentLevel'];
  readonly statesWithNoCsvExport: string[] = ['main.school.lists.studentLevel'];
  readonly statesWithNoBatchActions: string[] = ['main.school.lists.summer-school'];

  constructor(
    @Inject('$state') public $state: IStateService,
    private modalsService: ModalsService,
    private store: Store<any>,
    private imUser: ImUser,
    private csvExporterService: CsvExporterService,
  ) {
    //
  }

  ngOnInit(): void {
    this.isListActionsState = this.stateHasListActions();
    this.isListSummaryState = this.stateHasListSummary();
    this.isCsvExportState = this.stateHasCsvExport();
    this.isBatchActionsState = this.stateHasBatchActions();
    const schoolSlice = this.store.select(getSchool);
    schoolSlice.pipe(take(1)).subscribe((school: ISchool) => {
      this.school = school;
    });

    const currentUserSlice$ = this.store.select(getCurrentUser);
    this.currentUserSubscription = currentUserSlice$.pipe(take(1)).subscribe((user: IUser) => {
      const isEditingUser = this.imUser.isEditingUser(user);
      this.userCanEdit = isEditingUser;
      this.currentUser = user;
    });

    const batchActionsModeSlice$ = this.store.select(getBatchActionsMode);
    this.batchActionsModeSubscription = batchActionsModeSlice$
      .pipe(takeUntil(this.destroy$))
      .subscribe((mode: boolean) => {
        this.batchActionsMode = mode;
      });

    const selectedStudentIdsSlice$ = this.store.select(getBatchActionsSelectedStudentIds);
    this.batchActionSelectedStudentIdsSubscription = selectedStudentIdsSlice$
      .pipe(takeUntil(this.destroy$))
      .subscribe((studentIds: string[]) => {
        this.countStudents = studentIds.length;
      });

    const csvStoreData$ = this.store.select(getCsvDataState);
    this.csvStoreDataSubscription = csvStoreData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((csvStoreData: ICsvDataState) => {
        this.csvStoreData = csvStoreData;
      });
  }

  stateHasListActions(): boolean {
    return !includes(this.statesWithNoListActions, this.$state.current.name);
  }

  stateHasListSummary(): boolean {
    return !includes(this.statesWithNoListSummary, this.$state.current.name);
  }

  stateHasCsvExport(): boolean {
    return !includes(this.statesWithNoCsvExport, this.$state.current.name);
  }

  stateHasBatchActions(): boolean {
    return !includes(this.statesWithNoBatchActions, this.$state.current.name);
  }

  enableBatchActionsMode(): void {
    this.batchActionsMode = true;
    this.store.dispatch(new ToggleBatchActions(this.batchActionsMode));
  }

  exportMadLibCsv() {
    this.csvExporterService.exportListToCsv(this.csvStoreData);
  }

  // called when content-tools is passed a refactored list flag
  exportList(): void {
    this.csvExporterService.exportRefactoredListToCsv(this.listData);
  }

  export(): void {
    this.modalsService.openAlertModal({
      title: 'Coming soon!',
      message: '',
    });
  }
}
